"use client";

import { useEffect, useState } from "react";
import { Sun, Moon, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type ThemePreference = "auto" | "light" | "dark";

const STORAGE_KEY = "apnl-theme-mode";

/**
 * Time-based rule:
 * - Bright (Light) mode: 6:00 AM (06:00) to 6:59 PM (18:59)
 * - Dark mode: 7:00 PM (19:00) to 5:59 AM (05:59)
 */
export function isNightTime(date = new Date()): boolean {
  const hour = date.getHours();
  return hour >= 19 || hour < 6;
}

export function resolveTheme(pref: ThemePreference): "light" | "dark" {
  if (pref === "light") return "light";
  if (pref === "dark") return "dark";
  return isNightTime() ? "dark" : "light";
}

export function applyThemeToDocument(pref: ThemePreference): "light" | "dark" {
  const resolved = resolveTheme(pref);
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    if (resolved === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    root.setAttribute("data-theme-mode", pref);
  }
  return resolved;
}

export default function ThemeToggle() {
  const [preference, setPreference] = useState<ThemePreference>("auto");
  const [activeTheme, setActiveTheme] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const saved = (localStorage.getItem(STORAGE_KEY) as ThemePreference | null) || "auto";
    const validPref: ThemePreference =
      saved === "light" || saved === "dark" || saved === "auto" ? saved : "auto";
    setPreference(validPref);
    setActiveTheme(applyThemeToDocument(validPref));

    // Re-check every 60 seconds so "auto" mode flips automatically at 6:00 AM & 7:00 PM
    const interval = setInterval(() => {
      const currentPref =
        ((localStorage.getItem(STORAGE_KEY) as ThemePreference | null) || "auto");
      if (currentPref === "auto") {
        setActiveTheme(applyThemeToDocument("auto"));
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  const selectPreference = (nextPref: ThemePreference) => {
    setPreference(nextPref);
    localStorage.setItem(STORAGE_KEY, nextPref);
    setActiveTheme(applyThemeToDocument(nextPref));
  };

  if (!mounted) {
    return (
      <Button variant="ghost" size="icon" className="text-foreground/70" aria-label="Theme">
        <Sun className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-foreground/70 hover:text-primary"
          title={
            preference === "auto"
              ? `Auto Time Theme (${activeTheme === "dark" ? "Night / Dark" : "Day / Bright"})`
              : `Theme: ${preference === "dark" ? "Dark Mode" : "Bright Mode"}`
          }
          aria-label="Toggle theme"
        >
          {activeTheme === "dark" ? (
            <Moon className="h-5 w-5 text-amber-400 transition-transform" />
          ) : (
            <Sun className="h-5 w-5 text-amber-500 transition-transform" />
          )}
          {preference === "auto" && (
            <span
              className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-[8px] font-bold text-primary-foreground"
              title="Automatic time-based mode active"
            >
              A
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs font-semibold">
          Appearance
          <span className="block text-[11px] font-normal text-muted-foreground mt-0.5">
            Auto switches at 6:00 AM &amp; 7:00 PM
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => selectPreference("auto")}
          className="flex items-center justify-between cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            <span>Auto (Time-Based)</span>
          </span>
          {preference === "auto" && <span className="text-xs font-bold text-primary">✓</span>}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => selectPreference("light")}
          className="flex items-center justify-between cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <Sun className="h-4 w-4 text-amber-500" />
            <span>Bright Mode (Always)</span>
          </span>
          {preference === "light" && <span className="text-xs font-bold text-primary">✓</span>}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => selectPreference("dark")}
          className="flex items-center justify-between cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <Moon className="h-4 w-4 text-indigo-400" />
            <span>Dark Mode (Always)</span>
          </span>
          {preference === "dark" && <span className="text-xs font-bold text-primary">✓</span>}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
